import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  Filter,
  CheckCircle,
  XCircle,
  Eye,
  Trash2,
  UserCheck,
  Clock,
  AlertCircle,
  Plus,
  Save,
  Edit3,
  X,
  Star,
  Phone,
  Mail,
  MapPin,
  Briefcase,
  User,
  Award,
} from "lucide-react";
import { artisanService } from "@/services/firebase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
import toast from "react-hot-toast";
import type { Artisan, ArtisanFormData } from "@/types";

// Liste des métiers disponibles (correspond aux services seedés)
const AVAILABLE_SERVICES = [
  { value: "plomberie", label: "Plomberie" },
  { value: "electricite", label: "Électricité" },
  { value: "menuiserie", label: "Menuiserie" },
  { value: "peinture", label: "Peinture" },
  { value: "mecanique", label: "Mécanique auto" },
  { value: "climatisation", label: "Climatisation" },
  { value: "menage", label: "Ménage & nettoyage" },
  { value: "jardinage", label: "Jardinage" },
  { value: "travaux", label: "Travaux divers" },
];

// Liste des communes d'Abidjan
const AVAILABLE_CITIES = [
  { value: "cocody", label: "Cocody" },
  { value: "yopougon", label: "Yopougon" },
  { value: "marcory", label: "Marcory" },
  { value: "plateau", label: "Plateau" },
  { value: "adjame", label: "Adjamé" },
  { value: "abobo", label: "Abobo" },
  { value: "koumassi", label: "Koumassi" },
  { value: "treichville", label: "Treichville" },
  { value: "port-bouet", label: "Port-Bouët" },
];

const STATUS_CONFIG = {
  pending: { label: "En attente", variant: "warning" as const, icon: Clock, color: "text-amber-500", bg: "bg-amber-50" },
  verified: { label: "Vérifié", variant: "success" as const, icon: UserCheck, color: "text-green-500", bg: "bg-green-50" },
  suspended: { label: "Suspendu", variant: "danger" as const, icon: AlertCircle, color: "text-red-500", bg: "bg-red-50" },
  rejected: { label: "Rejeté", variant: "danger" as const, icon: XCircle, color: "text-red-500", bg: "bg-red-50" },
};

export function ArtisansAdminPage() {
  const [artisans, setArtisans] = useState<Artisan[]>([]);
  const [filtered, setFiltered] = useState<Artisan[]>([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [loading, setLoading] = useState(true);
  
  // Modal / Form states
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingArtisan, setEditingArtisan] = useState<Partial<Artisan> | null>(null);
  const [selectedArtisan, setSelectedArtisan] = useState<Artisan | null>(null);
  const [artisanToDelete, setArtisanToDelete] = useState<Artisan | null>(null);

  // Charger les artisans en temps réel
  useEffect(() => {
    setLoading(true);
    const unsubscribe = artisanService.subscribe((data) => {
      setArtisans(data);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Filtrage
  useEffect(() => {
    let result = [...artisans];
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (a) =>
          (a.firstName?.toLowerCase() || "").includes(q) ||
          (a.lastName?.toLowerCase() || "").includes(q) ||
          (a.service?.toLowerCase() || "").includes(q) ||
          (a.city?.toLowerCase() || "").includes(q) ||
          (a.phone || "").includes(q)
      );
    }
    if (statusFilter !== "all") {
      result = result.filter((a) => a.status === statusFilter);
    }
    setFiltered(result);
  }, [search, statusFilter, artisans]);

  // ─────────────────────────────────────────
  // FORMULAIRE
  // ─────────────────────────────────────────

  const getEmptyArtisan = (): Partial<Artisan> => ({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    service: "",
    serviceSlug: "",
    city: "",
    citySlug: "",
    bio: "",
    status: "pending",
    rating: 0,
    reviewCount: 0,
    active: true,
  });

  const openCreateForm = () => {
    setEditingArtisan(getEmptyArtisan());
    setIsEditing(false);
    setIsFormOpen(true);
  };

  const openEditForm = (artisan: Artisan) => {
    setEditingArtisan({ ...artisan });
    setIsEditing(true);
    setIsFormOpen(true);
    setSelectedArtisan(null); // Fermer le modal détail si ouvert
  };

  const handleSave = async () => {
    if (!editingArtisan) return;

    // Validation
    if (!editingArtisan.firstName?.trim() || !editingArtisan.lastName?.trim()) {
      toast.error("Le prénom et le nom sont obligatoires");
      return;
    }
    if (!editingArtisan.phone?.trim()) {
      toast.error("Le numéro de téléphone est obligatoire");
      return;
    }
    if (!editingArtisan.service?.trim()) {
      toast.error("Le métier est obligatoire");
      return;
    }
    if (!editingArtisan.city?.trim()) {
      toast.error("La commune est obligatoire");
      return;
    }

    try {
      if (isEditing && editingArtisan.id) {
        const { id, ...updateData } = editingArtisan;
        await artisanService.update(id, updateData as Partial<Artisan>);
        toast.success("Artisan mis à jour avec succès");
      } else {
        const formData: ArtisanFormData = {
          firstName: editingArtisan.firstName || "",
          lastName: editingArtisan.lastName || "",
          phone: editingArtisan.phone || "",
          email: editingArtisan.email || "",
          service: editingArtisan.service || "",
          serviceSlug: editingArtisan.serviceSlug || editingArtisan.service?.toLowerCase().replace(/\\s+/g, "-") || "",
          city: editingArtisan.city || "",
          citySlug: editingArtisan.citySlug || editingArtisan.city?.toLowerCase().replace(/\\s+/g, "-") || "",
          bio: editingArtisan.bio || "",
        };
        await artisanService.create(formData);
        toast.success("Artisan créé avec succès");
      }
      setIsFormOpen(false);
      setEditingArtisan(null);
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  // ─────────────────────────────────────────
  // ACTIONS
  // ─────────────────────────────────────────

  const handleVerify = async (id: string) => {
    try {
      await artisanService.verify(id, "admin");
      toast.success("Artisan vérifié avec succès");
    } catch {
      toast.error("Erreur lors de la vérification");
    }
  };

  const handleSuspend = async (id: string) => {
    try {
      await artisanService.suspend(id, "Suspension administrative");
      toast.success("Artisan suspendu");
    } catch {
      toast.error("Erreur lors de la suspension");
    }
  };

  const handleDelete = (artisan: Artisan) => {
    setArtisanToDelete(artisan);
  };

  const confirmDelete = async () => {
    if (!artisanToDelete) return;
    try {
      await artisanService.delete(artisanToDelete.id);
      toast.success("Artisan supprimé");
      if (selectedArtisan?.id === artisanToDelete.id) setSelectedArtisan(null);
      setArtisanToDelete(null);
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleToggleActive = async (artisan: Artisan) => {
    try {
      await artisanService.toggleActive(artisan.id, artisan.active !== false);
      toast.success(artisan.active !== false ? "Artisan désactivé" : "Artisan activé");
    } catch {
      toast.error("Erreur");
    } 
  };

  // ─────────────────────────────────────────
  // RENDER HELPERS
  // ─────────────────────────────────────────

  const renderFormFields = () => {
    if (!editingArtisan) return null;
    const a = editingArtisan;

    return (
      <div className="space-y-4">
        {/* Nom */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700">Prénom *</label>
            <Input
              value={a.firstName || ""}
              onChange={(e) => setEditingArtisan({ ...a, firstName: e.target.value })}
              placeholder="Prénom"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700">Nom *</label>
            <Input
              value={a.lastName || ""}
              onChange={(e) => setEditingArtisan({ ...a, lastName: e.target.value })}
              placeholder="Nom"
            />
          </div>
        </div>

        {/* Contact */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700">Téléphone *</label>
            <Input
              value={a.phone || ""}
              onChange={(e) => setEditingArtisan({ ...a, phone: e.target.value })}
              placeholder="+225 XX XX XX XX"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700">Email</label>
            <Input
              type="email"
              value={a.email || ""}
              onChange={(e) => setEditingArtisan({ ...a, email: e.target.value })}
              placeholder="email@exemple.com"
            />
          </div>
        </div>

        {/* Métier & Commune */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700">Métier *</label>
            <select
              value={a.service || ""}
              onChange={(e) => {
                const service = e.target.value;
                const serviceSlug = AVAILABLE_SERVICES.find(s => s.label === service)?.value || service.toLowerCase().replace(/\\s+/g, "-");
                setEditingArtisan({ ...a, service, serviceSlug });
              }}
              className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
            >
              <option value="">Choisir un métier...</option>
              {AVAILABLE_SERVICES.map((s) => (
                <option key={s.value} value={s.label}>{s.label}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700">Commune *</label>
            <select
              value={a.city || ""}
              onChange={(e) => {
                const city = e.target.value;
                const citySlug = AVAILABLE_CITIES.find(c => c.label === city)?.value || city.toLowerCase().replace(/\\s+/g, "-");
                setEditingArtisan({ ...a, city, citySlug });
              }}
              className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
            >
              <option value="">Choisir une commune...</option>
              {AVAILABLE_CITIES.map((c) => (
                <option key={c.value} value={c.label}>{c.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Bio */}
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-gray-700">Biographie / Description</label>
          <Textarea
            value={a.bio || ""}
            onChange={(e) => setEditingArtisan({ ...a, bio: e.target.value })}
            placeholder="Décrivez l'expérience et les compétences de l'artisan..."
            rows={4}
          />
        </div>

        {/* Statut (édition uniquement) */}
        {isEditing && (
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700">Statut actuel</label>
            <div className="flex items-center gap-2 p-3 rounded-lg bg-gray-50">
              {a.status && STATUS_CONFIG[a.status as keyof typeof STATUS_CONFIG] && (
                <>
                  {(() => {
                    const StatusIcon = STATUS_CONFIG[a.status as keyof typeof STATUS_CONFIG].icon;
                    return <StatusIcon className="w-4 h-4" />;
                  })()}
                  <Badge variant={STATUS_CONFIG[a.status as keyof typeof STATUS_CONFIG].variant}>
                    {STATUS_CONFIG[a.status as keyof typeof STATUS_CONFIG].label}
                  </Badge>
                </>
              )}
            </div>
            <p className="text-xs text-gray-400">
              Le statut peut être modifié via les actions Vérifier / Suspendre dans le tableau.
            </p>
          </div>
        )}
      </div>
    );
  };

  // ─────────────────────────────────────────
  // MAIN RENDER
  // ─────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-dark">Gestion des Artisans</h1>
          <p className="text-gray-500 text-sm">
            {artisans.length} artisan{artisans.length > 1 ? "s" : ""} enregistré{artisans.length > 1 ? "s" : ""}
            {statusFilter !== "all" && ` • Filtre: ${STATUS_CONFIG[statusFilter as keyof typeof STATUS_CONFIG]?.label}`}
          </p>
        </div>
        <Button onClick={openCreateForm} className="bg-primary hover:bg-primary-dark">
          <Plus className="w-4 h-4 mr-2" />
          Ajouter un artisan
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Rechercher par nom, métier, commune, téléphone..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {["all", "pending", "verified", "suspended"].map((status) => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    statusFilter === status
                      ? "bg-primary text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  {status === "all" ? "Tous" : STATUS_CONFIG[status as keyof typeof STATUS_CONFIG]?.label}
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Artisan</TableHead>
                  <TableHead>Métier</TableHead>
                  <TableHead>Commune</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Note</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12">
                      <div className="flex items-center justify-center gap-2 text-gray-400">
                        <Clock className="w-5 h-5 animate-spin" />
                        Chargement...
                      </div>
                    </TableCell>
                  </TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12 text-gray-400">
                      <User className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p>Aucun artisan trouvé</p>
                      {search || statusFilter !== "all" ? (
                        <p className="text-sm mt-1">Essayez de modifier vos filtres</p>
                      ) : (
                        <Button onClick={openCreateForm} className="mt-4 bg-primary hover:bg-primary-dark">
                          <Plus className="w-4 h-4 mr-2" />
                          Ajouter le premier artisan
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((artisan) => {
                    const status = STATUS_CONFIG[artisan.status];
                    const StatusIcon = status?.icon || Clock;
                    return (
                      <TableRow 
                        key={artisan.id}
                        className={artisan.active === false ? "opacity-50" : ""}
                      >
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                              {artisan.firstName?.[0]}{artisan.lastName?.[0]}
                            </div>
                            <div>
                              <p className="font-medium text-dark">
                                {artisan.firstName} {artisan.lastName}
                              </p>
                              {artisan.bio && (
                                <p className="text-xs text-gray-400 line-clamp-1 max-w-[200px]">
                                  {artisan.bio}
                                </p>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            <Briefcase className="w-3.5 h-3.5 text-gray-400" />
                            <span className="text-sm">{artisan.service}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            <MapPin className="w-3.5 h-3.5 text-gray-400" />
                            <span className="text-sm">{artisan.city}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-0.5">
                            <div className="flex items-center gap-1.5 text-sm">
                              <Phone className="w-3.5 h-3.5 text-gray-400" />
                              {artisan.phone}
                            </div>
                            {artisan.email && (
                              <div className="flex items-center gap-1.5 text-xs text-gray-400">
                                <Mail className="w-3 h-3" />
                                {artisan.email}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={status?.variant || "default"} className="text-xs">
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {status?.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                            <span className="font-medium text-sm">{artisan.rating || 0}</span>
                            <span className="text-gray-400 text-xs">({artisan.reviewCount || 0})</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {/* Vérifier */}
                            {artisan.status === "pending" && (
                              <button
                                onClick={() => handleVerify(artisan.id)}
                                className="p-2 text-green-500 hover:bg-green-50 rounded-lg transition-colors"
                                title="Vérifier"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </button>
                            )}
                            {/* Suspendre */}
                            {artisan.status === "verified" && (
                              <button
                                onClick={() => handleSuspend(artisan.id)}
                                className="p-2 text-amber-500 hover:bg-amber-50 rounded-lg transition-colors"
                                title="Suspendre"
                              >
                                <AlertCircle className="w-4 h-4" />
                              </button>
                            )}
                            {/* Voir détail */}
                            <button
                              onClick={() => setSelectedArtisan(artisan)}
                              className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Voir détails"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            {/* Éditer */}
                            <button
                              onClick={() => openEditForm(artisan)}
                              className="p-2 text-indigo-500 hover:bg-indigo-50 rounded-lg transition-colors"
                              title="Modifier"
                            >
                              <Edit3 className="w-4 h-4" />
                            </button>
                            {/* Supprimer */}
                            <button
                              onClick={() => handleDelete(artisan)}
                              className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              title="Supprimer"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* ─────────────────────────────────────────
          MODAL DÉTAIL ARTISAN
          ───────────────────────────────────────── */}
      <AnimatePresence>
        {selectedArtisan && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedArtisan(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-start gap-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center text-white font-bold text-xl flex-shrink-0">
                  {selectedArtisan.firstName?.[0]}{selectedArtisan.lastName?.[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-xl font-bold text-dark">
                    {selectedArtisan.firstName} {selectedArtisan.lastName}
                  </h3>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <Badge variant={STATUS_CONFIG[selectedArtisan.status]?.variant || "default"}>
                      {STATUS_CONFIG[selectedArtisan.status]?.label}
                    </Badge>
                    {selectedArtisan.active === false && (
                      <Badge variant="secondary" className="text-xs">Inactif</Badge>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setSelectedArtisan(null)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              {/* Infos */}
              <div className="space-y-3 text-sm">
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-500 flex items-center gap-2">
                    <Phone className="w-4 h-4" /> Téléphone
                  </span>
                  <span className="font-medium">{selectedArtisan.phone}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-500 flex items-center gap-2">
                    <Mail className="w-4 h-4" /> Email
                  </span>
                  <span className="font-medium">{selectedArtisan.email || "Non spécifié"}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-500 flex items-center gap-2">
                    <Briefcase className="w-4 h-4" /> Métier
                  </span>
                  <span className="font-medium">{selectedArtisan.service}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-500 flex items-center gap-2">
                    <MapPin className="w-4 h-4" /> Commune
                  </span>
                  <span className="font-medium">{selectedArtisan.city}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-500 flex items-center gap-2">
                    <Star className="w-4 h-4" /> Note
                  </span>
                  <span className="font-medium">
                    {selectedArtisan.rating || 0}/5 ({selectedArtisan.reviewCount || 0} avis)
                  </span>
                </div>
                {selectedArtisan.verifiedAt && (
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-500 flex items-center gap-2">
                      <Award className="w-4 h-4" /> Vérifié le
                    </span>
                    <span className="font-medium">
                      {new Date(selectedArtisan.verifiedAt?.toString()).toLocaleDateString("fr-FR")}
                    </span>
                  </div>
                )}
                {selectedArtisan.bio && (
                  <div className="py-3">
                    <span className="text-gray-500 block mb-2 font-medium">Biographie</span>
                    <p className="text-gray-700 bg-gray-50 p-3 rounded-lg text-sm leading-relaxed">
                      {selectedArtisan.bio}
                    </p>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-3 mt-6">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setSelectedArtisan(null)}
                >
                  Fermer
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    openEditForm(selectedArtisan);
                  }}
                >
                  <Edit3 className="w-4 h-4 mr-2" />
                  Modifier
                </Button>
                {selectedArtisan.status === "pending" && (
                  <Button
                    className="flex-1 bg-green-500 hover:bg-green-600"
                    onClick={() => {
                      handleVerify(selectedArtisan.id);
                      setSelectedArtisan(null);
                    }}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Vérifier
                  </Button>
                )}
                {selectedArtisan.status === "verified" && (
                  <Button
                    variant="default"
                    className="flex-1"
                    onClick={() => {
                      handleSuspend(selectedArtisan.id);
                      setSelectedArtisan(null);
                    }}
                  >
                    <AlertCircle className="w-4 h-4 mr-2" />
                    Suspendre
                  </Button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ─────────────────────────────────────────
          MODAL CONFIRMATION SUPPRESSION
          ───────────────────────────────────────── */}
      <AnimatePresence>
        {artisanToDelete && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4"
            onClick={() => setArtisanToDelete(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-6 max-w-sm w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-bold text-dark mb-2">Confirmer la suppression</h3>
              <p className="text-sm text-gray-500 mb-6">
                Êtes-vous sûr de vouloir supprimer <strong>{artisanToDelete.firstName} {artisanToDelete.lastName}</strong> ? Cette action est irréversible.
              </p>
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={() => setArtisanToDelete(null)}>Annuler</Button>
                <Button className="flex-1 bg-red-500 hover:bg-red-600" onClick={confirmDelete}>Supprimer</Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ─────────────────────────────────────────
          MODAL CRÉATION / ÉDITION
          ───────────────────────────────────────── */}
      <AnimatePresence>
        {isFormOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto"
            onClick={() => setIsFormOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-dark">
                  {isEditing ? "Modifier l'artisan" : "Nouvel artisan"}
                </h3>
                <button
                  onClick={() => setIsFormOpen(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {renderFormFields()}

              <div className="flex gap-3 mt-6">
                <Button
                  onClick={handleSave}
                  className="flex-1 bg-primary hover:bg-primary-dark"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {isEditing ? "Mettre à jour" : "Créer"}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setIsFormOpen(false)}
                  className="flex-1"
                >
                  Annuler
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}